import React, {useEffect, useState} from "react";
import {submitRatting} from "../../services/rating";
import {useNavigate} from "react-router-dom";
import {GoogleAddress} from "../GoogleAddress";
import {getContractorDashboardService} from "../../services/contractorServices";
import {jobIssuesOptions} from "../../constant";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";

const SubmitRating = () => {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		jobAddress: "",
		jobIssues: [],
		jobRating: 0,
		jobDate: "",
	});

	const [searchResults, setSearchResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const [limits, setLimits] = useState({});
	const [addressFlag, setAddressFlag] = useState("");

	const contractorStats = {
		ratingsToday: 3,
		ratingsTotal: 45,
		lastRatingForAddress: null, // Date or null
	};

	// Mock Google Places API search
	const searchJobSites = async (address) => {
		if (address.length < 3) {
			setSearchResults([]);
			return;
		}

		setIsSearching(true);

		// Simulate Google Places API call
		setTimeout(() => {
			// const mockResults = [
			//   {
			//     id: 1,
			//     address: `${address}, New York, NY 10001, USA`,
			//     verified: true,
			//     placeId: 'ChIJd8BlQ2BZwokRAFUEcm_qrcA'
			//   },
			//   {
			//     id: 2,
			//     address: `${address} Street, Brooklyn, NY 11201, USA`,
			//     verified: true,
			//     placeId: 'ChIJd8BlQ2BZwokRAFUEcm_qrcB'
			//   },
			//   {
			//     id: 3,
			//     address: `${address} Avenue, Queens, NY 11101, USA`,
			//     verified: true,
			//     placeId: 'ChIJd8BlQ2BZwokRAFUEcm_qrcC'
			//   }
			// ];
			// setSearchResults(mockResults);
			setIsSearching(false);
		}, 500);
	};

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear errors when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleIssueToggle = (issueId) => {
		setFormData((prev) => ({
			...prev,
			jobIssues: prev.jobIssues.includes(issueId) ? prev.jobIssues.filter((id) => id !== issueId) : [...prev.jobIssues, issueId],
		}));
	};

	const handleRatingChange = (rating) => {
		setFormData((prev) => ({
			...prev,
			jobRating: rating,
		}));

		// Clear errors when user selects rating
		if (errors.jobRating) {
			setErrors((prev) => ({
				...prev,
				jobRating: null,
			}));
		}
	};

	const handleAddressSelect = (address) => {
		setFormData((prev) => ({
			...prev,
			jobAddress: address.address,
		}));
		setSearchResults([]);

		if (errors.jobAddress) {
			setErrors((prev) => ({
				...prev,
				jobAddress: null,
			}));
		}
	};

	const validateForm = () => {
		const newErrors = {};

		// Job Address validation
		if (!formData.jobAddress.trim()) {
			newErrors.jobAddress = "Job address is required";
		}

		// Job Rating validation
		if (!formData.jobRating || formData.jobRating === 0) {
			newErrors.jobRating = "Overall job rating is required";
		}

		// Job Date validation
		if (!formData.jobDate) {
			newErrors.jobDate = "Job date is required";
		} else {
			const jobDate = new Date(formData.jobDate);
			const today = new Date();

			if (jobDate > today) {
				newErrors.jobDate = "Job date cannot be in the future";
			}
		}

		// Rate limiting validation
		if (contractorStats.ratingsToday >= 15) {
			newErrors.general = "You have reached the daily limit of 15 ratings";
		}

		if (contractorStats.ratingsTotal >= 100) {
			newErrors.general = "You have reached the maximum limit of 100 total ratings";
		}

		// Check if rating for same address within 30 days
		if (contractorStats.lastRatingForAddress) {
			const lastRating = new Date(contractorStats.lastRatingForAddress);
			const daysDiff = Math.floor((new Date() - lastRating) / (1000 * 60 * 60 * 24));

			if (daysDiff < 30) {
				newErrors.general = `You can only rate the same address once every 30 days. Last rating was ${daysDiff} days ago.`;
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const calculateJobDateStatus = () => {
		if (!formData.jobDate) return null;

		const jobDate = new Date(formData.jobDate);
		const today = new Date();
		const daysDiff = Math.floor((today - jobDate) / (1000 * 60 * 60 * 24));

		if (daysDiff <= 90) {
			return {type: "fresh", days: daysDiff};
		} else {
			return {type: "historic", days: daysDiff};
		}
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Simulate API call
			await new Promise((resolve) => setTimeout(resolve, 2000));

			const dateStatus = calculateJobDateStatus();
			const requestBody = {
				address: formData.jobAddress,
				job_issues: formData.jobIssues,
				stars: formData.jobRating,
				job_date: formData.jobDate,
				addressFlag: addressFlag,
				// place_id : 'Abc12345', // Mock place ID, replace with actual if needed
				zip_code: "10001", // Mock zip code, replace with actual if needed
				city: "New York", // Mock city, replace with actual if needed
				state: "NY", // Mock state, replace with actual if needed
				country: "USA", // Mock country, replace with actual if needed
			};
			const response = await submitRatting(requestBody);
			if (response.status == true) {
				navigate("/contractor-dashboard/ratings");
				// Reset form
				setFormData({
					jobAddress: "",
					jobIssues: [],
					jobRating: 0,
					jobDate: "",
				});
			} else {
				setErrors((prev) => ({
					...prev,
					general: response.message || "Failed submitting rating",
				}));
				setIsSubmitting(false);
				sweetAlerts("error", response.message || "Failed submitting rating");

				// sweetAlerts("success", `Rating submitted successfully! This address will be flagged with "Previous History" due to the job date being ${dateStatus.days} days ago.`);

				return;
			}
			// Show success message with appropriate context
			if (dateStatus?.type === "historic") {
				setIsSubmitting(false);
				sweetAlerts("success", `Rating submitted successfully! This address will be flagged with "Previous History" due to the job date being ${dateStatus.days} days ago.`);
			} else {
				setIsSubmitting(false);
				sweetAlerts("success", "Rating submitted successfully!");
			}
		} catch (error) {
			isFormValid()
			setIsSubmitting(false);
			sweetAlerts("error", "Error submitting rating. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFormValid = () => {
		return formData.jobAddress && formData.jobRating > 0 && formData.jobDate && !errors.general;
	};

	const dateStatus = calculateJobDateStatus();

	const getData = async () => {
		try {
			const response = await getContractorDashboardService({});
			setLimits(response?.data);
		} catch (error) {}
	};

	useEffect(() => {
		getData();
	}, []);

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>Submit New Rating</h2>
				<p>Rate your experience working at a job site</p>
			</div>

			{/* Rate Limiting Alerts */}
			{/* {contractorStats.ratingsTotal >= 30 && contractorStats.ratingsTotal < 100 && (
				<div className="alert alert-warning">
					<strong>Notice:</strong> You have submitted {contractorStats.ratingsTotal} ratings. Maximum limit is 100 ratings total.
				</div>
			)} */}

			{errors.general && (
				<div className="alert alert-danger">
					<strong>Error:</strong> {errors.general}
				</div>
			)}

			{/* Form Container */}
			<div className="form-container">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}>
					{/* 1. Job Address */}
					<div className="form-group">
						<label className="form-label">
							Job Address *<span className="field-hint">Google verified addresses only</span>
						</label>
						<div className="search-input-container">
							{/* <input
                type="text"
                className={`form-input ${errors.jobAddress ? 'error' : ''}`}
                placeholder="Start typing the job site address..."
                value={formData.jobAddress}
                onChange={(e) => {
                  handleInputChange('jobAddress', e.target.value);
                  searchJobSites(e.target.value);
                }}
              />
              {isSearching && (
                <div className="search-loading">
                  <div className="spinner"></div>
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(result => (
                    <div 
                      key={result.id}
                      className="search-result-item"
                      onClick={() => handleAddressSelect(result)}
                    >
                      <div className="result-address">{result.address}</div>
                      <div className="result-details">
                        <span className="result-verified">
                          {result.verified && <span className="verified-badge">✓ Google Verified</span>}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )} */}
							<GoogleAddress
								styleFlag="dashboardStyle"
								address={formData.jobAddress}
								callBackAction={(val) => {
									setFormData((prev) => ({...prev, jobAddress: val.value}));
									setAddressFlag(val.type);
								}}
							/>
						</div>
						{errors.jobAddress && <div className="error-message">{errors.jobAddress}</div>}
					</div>

					{/* 2. Job Issues */}
					<div className="form-group">
						<label className="form-label">
							What challenges did you face?
							<span className="field-hint">Select all that apply (optional)</span>
						</label>
						<div className="job-issues-grid">
							{jobIssuesOptions.map((issue) => (
								<label key={issue.id} className="issue-checkbox">
									<input type="checkbox" checked={formData.jobIssues.includes(issue.id)} onChange={() => handleIssueToggle(issue.id)} />
									<span className="checkbox-custom"></span>
									<span className="checkbox-label">{issue.label}</span>
								</label>
							))}
						</div>
					</div>

					{/* 3. Overall Job Rating */}
					<div className="form-group">
						<label className="form-label">
							Overall Job Rating *<span className="field-hint">Rate your overall experience (1-5 stars)</span>
						</label>
						<div className="rating-container">
							<div className="star-rating-large">
								{[1, 2, 3, 4, 5].map((star) => (
									<button key={star} className={`star-btn-large ${formData.jobRating >= star ? "filled" : ""}`} onClick={() => handleRatingChange(star)} type="button">
										★
									</button>
								))}
							</div>
							<div className="rating-text"></div>
						</div>
						{errors.jobRating && <div className="error-message">{errors.jobRating}</div>}
					</div>

					{/* 4. Job Date */}
					<div className="form-group">
						<label className="form-label">
							Job Date *<span className="field-hint">When did you work at this job site?</span>
						</label>
						<input
							type="date"
							className={`form-input ${errors.jobDate ? "error" : ""}`}
							value={formData.jobDate}
							onChange={(e) => handleInputChange("jobDate", e.target.value)}
							onClick={(e) => {e.target.showPicker && e.target.showPicker();}}
							max={new Date().toISOString().split("T")[0]}
						/>
						{errors.jobDate && <div className="error-message">{errors.jobDate}</div>}

						{/* Date Status Info */}
						{dateStatus && (
							<div className={`date-status ${dateStatus.type}`}>
								{dateStatus.type === "fresh" ? (
									<div className="status-fresh">
										<span className="status-icon">✓</span>
										<span>Fresh rating ({dateStatus.days} days ago)</span>
									</div>
								) : (
									<div className="status-historic">
										<span className="status-icon">⚠️</span>
										<span>Historic rating ({dateStatus.days} days ago) - This address will be flagged with "Previous History"</span>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Submit Button */}
					<div className="form-submit">
						<button type="submit" className="btn btn-primary btn-large" disabled={!isFormValid() || isSubmitting}>
							{isSubmitting ? (
								<>
									<div className="spinner-small"></div>
									Submitting Rating...
								</>
							) : (
								"Submit Rating"
							)}
						</button>
					</div>

					{/* Form Stats */}
					<div className="form-stats">
						<div className="stats-item">
							<strong>Today:</strong> {limits?.totalDailyRating}/15 ratings
						</div>
						<div className="stats-item">
							<strong>Total:</strong> {limits?.totalRating}/100 ratings
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default SubmitRating;
